/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
	Package,
	Truck,
	Info,
	ShoppingCart,
	Lock,
	User,
	MapPin,
	Phone,
	Mail,
} from "lucide-react";
import { usePlacesWidget } from "react-google-autocomplete";
import { PayPalButton } from "react-paypal-button-v2";

// Actions & Utilities (Ensure paths are correct)
import { FreeSwatches } from "../emails/new";
import {
	resetSwatchCart,
	saveSwatchShippingAddress,
} from "../actions/cartActions";
import { createSwatchOrder } from "../actions/orderActions";
import {
	addEmailToCartMailChimp,
	apiMail,
	getUserDetails,
} from "../actions/userActions";
import { FormInput } from "../components/FormInput";
import { getTaxRate } from "../utils/getTaxRate";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Toast } from "./customize/Toast";

const SHADE_LIMIT = 5;

const SwatchCheckout = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { swatchCartItems = [] } = useSelector((state) => state.swatchCart);
	const { userInfo, isGuest } = useSelector((state) => state.userLogin);
	const fabricCart = useSelector(
		(state) => state.swatchCart?.swatchCartItems || []
	);
	// --- State ---
	const [shippingType, setShippingType] = useState("Standard");
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [paypalError, setPaypalError] = useState(null);
	const [toast, setToast] = useState(null);
	// *** State to control PayPal Button rendering ***
	const [showPaypal, setShowPaypal] = useState(false);

	const [formData, setFormData] = useState({
		/* ... initial form data ... */
		firstName: userInfo?.name?.split(" ")[0] || "",
		lastName: userInfo?.name?.split(" ").slice(1).join(" ") || "",
		email: userInfo?.email || "",
		phoneNumber: "",
		addressLine1: "",
		addressLine2: "",
		city: "",
		province: "",
		country: "Canada",
		postalCode: "",
		howDidYouHear: "",
	});

	// --- Effects ---
	useEffect(() => {
		/* ... pre-fill user details ... */
		if (userInfo && !isGuest && !formData.firstName) {
			setFormData((prev) => ({
				...prev,
				firstName: userInfo.name?.split(" ")[0] || "",
				lastName: userInfo.name?.split(" ").slice(1).join(" ") || "",
				email: userInfo.email || "",
			}));
		}
	}, [dispatch, userInfo, navigate, isGuest, formData.firstName]);

	useEffect(() => {
		setPaypalError(null);
		// *** Control PayPal visibility based on shipping type ***
		setShowPaypal(shippingType === "Express");
	}, [shippingType]);

	// --- Google Places ---
	const { ref: placesRef } = usePlacesWidget({
		/* ... google places config ... */
		apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
		onPlaceSelected: (place) => {
			if (!place?.address_components) return;
			const getComponent = (type) =>
				place.address_components.find((c) => c.types.includes(type));
			const streetNumber = getComponent("street_number")?.long_name || "";
			const street = getComponent("route")?.long_name || "";
			const city = getComponent("locality")?.long_name || "";
			const province =
				getComponent("administrative_area_level_1")?.short_name || "";
			const postalCode = getComponent("postal_code")?.long_name || "";
			const countryCode = getComponent("country")?.short_name?.toUpperCase();
			setFormData((prev) => ({
				...prev,
				addressLine1: `${streetNumber} ${street}`.trim(),
				city,
				province,
				postalCode,
				country: countryCode === "US" ? "USA" : "Canada",
			}));
			setErrors((prev) => ({
				...prev,
				addressLine1: undefined,
				city: undefined,
				province: undefined,
				postalCode: undefined,
			}));
		},
		options: {
			types: ["address"],
			componentRestrictions: {
				country: formData.country === "USA" ? "us" : "ca",
			},
			fields: ["address_components"],
		},
	});

	// --- Validation ---
	const validateForm = useCallback(() => {
		/* ... validation logic (same as before) ... */
		const newErrors = {};
		const requiredFields = [
			"firstName",
			"lastName",
			"email",
			"phoneNumber",
			"addressLine1",
			"city",
			"province",
			"postalCode" /* "howDidYouHear" - make optional? */,
		];
		requiredFields.forEach((field) => {
			if (!formData[field]?.trim()) {
				newErrors[field] = "This field is required";
			}
		});
		if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}
		const phoneDigits = formData.phoneNumber?.replace(/\D/g, "");
		if (formData.phoneNumber && phoneDigits.length !== 10) {
			newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
		}
		if (
			formData.country === "Canada" &&
			formData.postalCode &&
			!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(formData.postalCode)
		) {
			newErrors.postalCode =
				"Invalid Canadian Postal Code format (e.g., A1A 1A1)";
		} else if (
			formData.country === "USA" &&
			formData.postalCode &&
			!/^\d{5}(-\d{4})?$/.test(formData.postalCode)
		) {
			newErrors.postalCode =
				"Invalid US Zip Code format (e.g., 12345 or 12345-6789)";
		}
		if (formData.province && formData.province.length !== 2) {
			newErrors.province = "Please use 2-letter abbreviation";
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [formData]);

	// --- Input Change ---
	const handleInputChange = useCallback(
		(e) => {
			/* ... input change logic (same as before) ... */
			const { name, value } = e.target;
			setFormData((prev) => ({ ...prev, [name]: value }));
			if (errors[name]) {
				setErrors((prev) => ({ ...prev, [name]: undefined }));
			}
			if (name === "country") {
				setErrors((prev) => ({
					...prev,
					province: undefined,
					postalCode: undefined,
				}));
			}
		},
		[errors]
	);

	// --- Order Submission Logic ---
	const handleOrder = useCallback(
		async (paymentInfo = null) => {
			/* ... order creation, email, mailchimp, redirect logic (same as before) ... */
			setIsSubmitting(true);
			setToast(null);
			if (!validateForm()) {
				setIsSubmitting(false);
				window.scrollTo({ top: 0, behavior: "smooth" });
				return;
			}
			if (swatchCartItems.length === 0) {
				setToast({ message: "Your sample cart is empty.", type: "error" });
				setIsSubmitting(false);
				return;
			}
			try {
				const taxRate = getTaxRate(formData.province);
				const shippingCost = shippingType === "Standard" ? 0 : 15.0;
				const taxAmount = shippingCost * taxRate;
				const finalPrice = shippingCost + taxAmount;
				const orderData = {
					swatchCartItems,
					swatchShippingAddress: {
						...formData,
						price: finalPrice.toFixed(2),
						shippingType: shippingType,
						taxRate: taxRate,
						paymentResult: paymentInfo,
					},
					userInfo: userInfo,
				};
				const insertedId = await dispatch(createSwatchOrder(orderData));
				if (!insertedId) {
					throw new Error("Order creation failed: No Order ID returned.");
				}
				const productList = swatchCartItems.map((item) => ({
					name: `${item.item.ocs_name || item.item.series} - ${
						item.item.ocs_colour_name || item.item.colour
					}`,
					img: item.item.image
						? encodeURI(
								`https://offcutshades.com/${item.item.image.toLowerCase()}`
						  )
						: "",
				}));
				const fullAddress = `${formData.addressLine1}${
					formData.addressLine2 ? `, ${formData.addressLine2}` : ""
				}, ${formData.city}, ${formData.province}, ${formData.postalCode}, ${
					formData.country
				}`;
				await apiMail(
					formData.email,
					"Your Off Cut Shades Sample Order Confirmation",
					<FreeSwatches
						orderNumber={insertedId}
						orderDate={new Date()}
						productList={productList}
						address={fullAddress}
						phoneNumber={formData.phoneNumber}
						shippingType={shippingType}
						shippingCost={shippingCost > 0 ? finalPrice.toFixed(2) : "Free"}
						name={`${formData.firstName} ${formData.lastName}`}
						isExpress={shippingType === "Express"}
					/>
				);
				await addEmailToCartMailChimp(
					formData.email,
					"Swatches Ordered",
					formData.firstName,
					formData.lastName
				);
				await dispatch(resetSwatchCart());
				setToast({
					message: "Order placed successfully! Check your email.",
					type: "success",
				});
				setTimeout(() => {
					navigate("/swatch-order-success", { state: { orderId: insertedId } });
				}, 1500);
			} catch (error) {
				console.error("Order submission failed:", error);
				setToast({
					message: `Order submission failed: ${
						error.message || "Please try again."
					}`,
					type: "error",
				});
				setIsSubmitting(false);
			}
		},
		[
			dispatch,
			formData,
			shippingType,
			swatchCartItems,
			userInfo,
			validateForm,
			navigate,
		]
	);

	// --- Form Submit Button Handler (For BOTH Standard and initiating Express) ---
	const handleSubmitOrProceed = (e) => {
		e.preventDefault();
		setPaypalError(null); // Clear previous PayPal errors

		if (!validateForm()) {
			window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll up to show errors
			return; // Stop if validation fails
		}
		if (swatchCartItems.length === 0) {
			setToast({ message: "Your sample cart is empty.", type: "error" });
			return;
		}

		if (shippingType === "Express") {
			// If Express, simply ensure PayPal button is visible (state already handles this)
			// The actual payment initiation happens via the PayPal button itself
			console.log("Proceeding to PayPal for Express Shipping.");
			// Optionally scroll to PayPal button if needed:
			// document.getElementById('paypal-button-container')?.scrollIntoView({ behavior: 'smooth' });
		} else {
			// If Standard (Free), call the order handler directly
			handleOrder();
		}
	};

	// --- PayPal Button v2 Handlers ---
	// onSuccess callback for react-paypal-button-v2
	const successPaymentHandler = (details, data) => {
		// `details` holds the captured order info
		console.log("PayPal Payment Successful:", details);
		setPaypalError(null); // Clear errors
		// `details` object structure is slightly different from v3's capture() result
		// Ensure you extract necessary info if needed (like payer details, transaction ID)
		// Pass the `details` object as paymentInfo to handleOrder
		handleOrder(details);
	};

	// onError callback for react-paypal-button-v2
	const errorPaymentHandler = (err) => {
		console.error("PayPal Button v2 Error:", err);
		const errorMessage =
			typeof err === "string"
				? err
				: err?.message || "An unknown PayPal error occurred.";
		setPaypalError(`Payment failed: ${errorMessage}`);
		setToast({
			message: `Payment failed. Please check your details or try again.`,
			type: "error",
		});
		setIsSubmitting(false); // Ensure submitting state is reset
	};

	// onCancel callback for react-paypal-button-v2
	const cancelPaymentHandler = (data) => {
		console.log("PayPal Payment Cancelled:", data);
		setToast({ message: "Payment cancelled.", type: "info" });
		// Optionally reset submitting state if it was set before cancellation
		// setIsSubmitting(false);
	};

	// --- Render Components ---
	const CartPreview = () => {
		/* ... CartPreview JSX (same as before) ... */
		return (
			<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-5">
				{" "}
				<h3 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
					{" "}
					<ShoppingCart size={18} className="text-primary" /> Your Samples (
					{fabricCart.length}/{SHADE_LIMIT}){" "}
				</h3>{" "}
				<div
					className={`space-y-3 ${
						fabricCart.length > 3 ? "max-h-60 overflow-y-auto pr-2" : ""
					}`}
				>
					{" "}
					{fabricCart.length === 0 ? (
						<p className="text-sm text-gray-500 text-center py-4">
							Your sample selections will appear here.
						</p>
					) : (
						swatchCartItems.map((cartItem, index) => {
							const item = cartItem.item;
							return (
								<div
									key={`${item.code}-${index}`}
									className="flex items-center gap-3"
								>
									{" "}
									<img
										src={item.image}
										alt={item.ocs_colour_name || item.name}
										className="w-12 h-12 rounded-md object-cover border border-gray-200 flex-shrink-0"
									/>{" "}
									<div className="flex-1 min-w-0">
										{" "}
										<p
											className="font-medium text-xs truncate"
											title={item.ocs_name || item.series}
										>
											{item.ocs_name || item.series}
										</p>{" "}
										<p
											className="text-xs text-gray-500 truncate"
											title={item.ocs_colour_name || item.colour}
										>
											{item.ocs_colour_name || item.colour}
										</p>{" "}
									</div>{" "}
								</div>
							);
						})
					)}{" "}
				</div>{" "}
				{fabricCart.length > 0 && (
					<Link
						to="/swatches"
						className="text-xs text-primary hover:underline mt-3 inline-block"
					>
						Edit Samples
					</Link>
				)}{" "}
			</div>
		);
	};

	const ShippingSelector = () => {
		/* ... ShippingSelector JSX (same as before) ... */
		return (
			<div className="space-y-3">
				{" "}
				{/* Standard */}{" "}
				<div
					className={`p-4 border-2 rounded-xl cursor-pointer transition-all flex items-center gap-4 ${
						shippingType === "Standard"
							? "border-primary bg-primary/5 ring-2 ring-primary/20"
							: "border-gray-200 hover:border-gray-300 bg-white"
					}`}
					onClick={() => setShippingType("Standard")}
				>
					{" "}
					<input
						type="radio"
						name="shippingType"
						value="Standard"
						checked={shippingType === "Standard"}
						onChange={() => setShippingType("Standard")}
						className="w-4 h-4 text-primary focus:ring-primary/50"
					/>{" "}
					<Package className="w-6 h-6 text-primary flex-shrink-0" />{" "}
					<div className="flex-1">
						{" "}
						<p className="font-medium">Standard Shipping</p>{" "}
						<p className="text-sm text-gray-500">
							FREE via Canada Post (Est. 3-9 business days)
						</p>{" "}
					</div>{" "}
				</div>{" "}
				{/* Express */}{" "}
				<div
					className={`p-4 border-2 rounded-xl cursor-pointer transition-all flex items-center gap-4 ${
						shippingType === "Express"
							? "border-primary bg-primary/5 ring-2 ring-primary/20"
							: "border-gray-200 hover:border-gray-300 bg-white"
					}`}
					onClick={() => setShippingType("Express")}
				>
					{" "}
					<input
						type="radio"
						name="shippingType"
						value="Express"
						checked={shippingType === "Express"}
						onChange={() => setShippingType("Express")}
						className="w-4 h-4 text-primary focus:ring-primary/50"
					/>{" "}
					<Truck className="w-6 h-6 text-primary flex-shrink-0" />{" "}
					<div className="flex-1">
						{" "}
						<p className="font-medium">Express Shipping</p>{" "}
						<p className="text-sm text-gray-500">
							{" "}
							$15 + Tax via Courier (Est. 1-3 business days).{" "}
							<span className="block text-xs text-blue-600">
								{" "}
								Fully refundable upon blind purchase!
							</span>{" "}
						</p>{" "}
					</div>{" "}
				</div>{" "}
			</div>
		);
	};

	// --- Calculated Values for Display ---
	const taxRate = getTaxRate(formData.province);
	const shippingCost = shippingType === "Express" ? 15.0 : 0;
	const taxAmount = shippingCost * taxRate;
	const orderTotal = shippingCost + taxAmount;
	const currencyCode = formData.country === "USA" ? "USD" : "CAD";

	// --- Main Render ---
	return (
		<div className="min-h-screen bg-gray-100 py-8 md:py-12">
			<div className="container mx-auto px-4 max-w-screen-lg">
				<motion.div className="text-center mb-8 md:mb-12">
					<h1 className="text-3xl md:text-4xl font-bold text-gray-900">
						Checkout Samples
					</h1>
					<p className="text-gray-600 mt-2">
						Confirm your details to receive your fabric samples.
					</p>
				</motion.div>

				{/* --- Left Column: Form --- */}
				<form
					onSubmit={handleSubmitOrProceed}
					className="space-y-6 grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6 md:gap-8 items-start"
				>
					<div className="space-y-6">
						{/* Cart Summary (Mobile Only) */}
						<div className="lg:hidden">
							{" "}
							<CartPreview />{" "}
						</div>
						{/* Shipping Information Card */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
							<h3 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
								{" "}
								<MapPin size={18} className="text-primary" /> Shipping
								Information{" "}
							</h3>
							<div className="space-y-4">
								{/* ... FormInput fields for name, email, phone, address etc. (same as before) ... */}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{" "}
									<FormInput
										label="First Name"
										name="firstName"
										value={formData.firstName}
										onChange={handleInputChange}
										error={errors.firstName}
										required
									/>{" "}
									<FormInput
										label="Last Name"
										name="lastName"
										value={formData.lastName}
										onChange={handleInputChange}
										error={errors.lastName}
										required
									/>{" "}
								</div>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{" "}
									<FormInput
										label="Email"
										type="email"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										error={errors.email}
										required
										icon={<Mail size={16} className="text-gray-400" />}
									/>{" "}
									<FormInput
										label="Phone Number"
										type="tel"
										name="phoneNumber"
										value={formData.phoneNumber}
										onChange={handleInputChange}
										error={errors.phoneNumber}
										required
										icon={<Phone size={16} className="text-gray-400" />}
										placeholder="e.g., 5551234567"
									/>{" "}
								</div>
								<div>
									{" "}
									<label
										htmlFor="country"
										className="block text-sm font-medium mb-1 text-gray-700"
									>
										Country
									</label>{" "}
									<select
										id="country"
										name="country"
										value={formData.country}
										onChange={handleInputChange}
										className="form-select"
									>
										{" "}
										<option value="Canada">Canada</option>{" "}
										<option value="USA">United States</option>{" "}
									</select>{" "}
								</div>
								<div>
									{" "}
									<FormInput
										label="Street Address"
										ref={placesRef}
										name="addressLine1"
										value={formData.addressLine1}
										onChange={handleInputChange}
										error={errors.addressLine1}
										required
										placeholder="Start typing your address..."
									/>{" "}
								</div>
								<FormInput
									label="Apt, suite, etc. (Optional)"
									name="addressLine2"
									value={formData.addressLine2}
									onChange={handleInputChange}
								/>
								<div className="grid grid-cols-1 sm:grid-cols-[2fr,1fr,1fr] gap-4">
									{" "}
									<FormInput
										label="City"
										name="city"
										value={formData.city}
										onChange={handleInputChange}
										error={errors.city}
										required
									/>{" "}
									<FormInput
										label={formData.country === "USA" ? "State" : "Province"}
										name="province"
										maxLength={2}
										value={formData.province}
										onChange={handleInputChange}
										error={errors.province}
										required
										placeholder="e.g., ON or NY"
									/>{" "}
									<FormInput
										label={
											formData.country === "USA" ? "Zip Code" : "Postal Code"
										}
										name="postalCode"
										value={formData.postalCode}
										onChange={handleInputChange}
										error={errors.postalCode}
										required
									/>{" "}
								</div>
							</div>
						</div>
						{/* Shipping Method Card */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
							<h3 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
								{" "}
								<Truck size={18} className="text-primary" /> Shipping Method{" "}
							</h3>
							<ShippingSelector />
						</div>
						{/* Additional Info Card */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
							<h3 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
								{" "}
								<Info size={18} className="text-primary" /> Additional Info{" "}
							</h3>
							<div>
								<label
									htmlFor="howDidYouHear"
									className="block text-sm font-medium mb-1 text-gray-700"
								>
									How did you hear about us?{" "}
									<span className="text-gray-500">(Optional)</span>
								</label>
								<select
									aria-label="How did you hear about us?"
									name="howDidYouHear"
									id="howDidYouHear"
									value={formData.howDidYouHear}
									onChange={handleInputChange}
									className={`form-select ${
										errors.howDidYouHear ? "border-red-500" : "border-gray-300"
									}`}
								>
									<option value="">Select an option</option>
									{/* ... options ... */}
									<option value="Facebook">Facebook</option>{" "}
									<option value="Instagram">Instagram</option>{" "}
									<option value="Social Media">Other Social Media</option>{" "}
									<option value="Search Engine">Google/Search Engine</option>{" "}
									<option value="Advertisement">Online Ad</option>{" "}
									<option value="Word of mouth">Friend/Referral</option>{" "}
									<option value="Other">Other</option>
								</select>
								{errors.howDidYouHear && (
									<p className="text-red-500 text-xs mt-1">
										{errors.howDidYouHear}
									</p>
								)}
							</div>
						</div>
					</div>

					{/* --- Right Column: Cart Summary (Desktop) --- */}
					<div className="space-y-6 sticky top-6">
						<div className="hidden lg:block">
							<CartPreview />
						</div>

						{/* --- Submit Section (Combined Button/PayPal) --- */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
							{" "}
							{/* Added mt-6 */}
							<h3 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100">
								{shippingType === "Express" ? "Payment" : "Complete Order"}
							</h3>
							{/* Mini Order Summary */}
							<div className="space-y-1 text-sm mb-5">
								<div className="flex justify-between">
									<span className="text-gray-600">
										Samples ({swatchCartItems.length})
									</span>
									<span>Free</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">
										Shipping ({shippingType})
									</span>
									<span>
										{shippingCost === 0
											? "Free"
											: `$${shippingCost.toFixed(2)}`}
									</span>
								</div>
								{shippingCost > 0 && (
									<div className="flex justify-between">
										<span className="text-gray-600">
											Tax ({(taxRate * 100).toFixed(0)}%)
										</span>
										<span>${taxAmount.toFixed(2)}</span>
									</div>
								)}
								<div className="border-t border-gray-200 mt-2 pt-2">
									{" "}
									<div className="flex justify-between font-semibold text-base">
										<span>Total</span>
										<span>
											{orderTotal === 0
												? "Free"
												: `$${orderTotal.toFixed(2)} ${currencyCode}`}
										</span>
									</div>{" "}
								</div>
							</div>
							{/* --- Conditional Buttons --- */}
							{/* Render PayPal button ONLY if Express is selected */}
							{shippingType === "Express" ? (
								<div id="paypal-button-container">
									{" "}
									{/* Container for PayPal button */}
									<p className="text-xs text-gray-600 mb-3 text-center">
										Complete payment with PayPal for Express Shipping.
									</p>
									{paypalError && (
										<p className="text-red-500 text-xs text-center mb-2">
											{paypalError}
										</p>
									)}
									{isSubmitting ? (
										<div className="flex justify-center items-center p-4">
											<LoadingSpinner size="md" />
										</div>
									) : (
										<PayPalButton
											amount={orderTotal.toFixed(2)}
											currency={currencyCode}
											onSuccess={successPaymentHandler}
											onError={errorPaymentHandler}
											onCancel={cancelPaymentHandler}
											options={{
												clientId:
													process.env.NODE_ENV === "production"
														? process.env.REACT_APP_PRODUCTION_PAYPAL_ID
														: process.env.REACT_APP_SANDBOX_PAYPAL_ID,
												currency: currencyCode,
												// Add other v2 options if needed
											}}
											style={{ layout: "vertical", shape: "pill", height: 45 }}
										/>
									)}
								</div>
							) : (
								// Render standard submit button for Free shipping
								<button
									type="submit" // Submits the form -> handleFreeSubmit -> handleOrder
									disabled={isSubmitting || swatchCartItems.length === 0}
									className="w-full btn btn-primary"
								>
									{isSubmitting ? (
										<LoadingSpinner size="sm" />
									) : (
										<>
											<Package size={18} /> Complete Free Order
										</>
									)}
								</button>
							)}
						</div>
						{/* Optional: Add trust badges or guarantees here */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center text-xs text-gray-500">
							<Lock size={16} className="inline-block mr-1 mb-0.5" /> Secure
							Checkout
						</div>
					</div>
				</form>

				{/* Toast Notifications */}
				<AnimatePresence>
					{" "}
					{toast && (
						<Toast
							show={!!toast}
							type={toast.type}
							message={toast.message}
							onClose={() => setToast(null)}
							duration={4000}
						/>
					)}{" "}
				</AnimatePresence>
			</div>
		</div>
	);
};

export default SwatchCheckout;
